import { useState } from 'react';

import { getErrorString } from '../../../util/normalize-errors';
import { IDENTITY_RELATIONSHIP } from '../../../constants/identity';
import { mapWaldogramConfigToPhotosInput } from '../../../components/global/WaldogramConfigurator/helpers';

const useWaldogramCheckout = ({ checkoutMutation }) => {
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);

  /**
   * Checks out & configures the Waldogram in one go.
   *
   * @param {Object} options - The options.
   * @param {Object} [options.stripeToken] - The stripe token (id, email).
   * @param {Object} options.account - The user's account.
   * @param {Object} options.waldogram - The custom print to be purchased.
   * @param {Object} options.config - The waldogram user config.
   *
   * @returns {Promise<void>}
   */
  const onCheckout = async ({ stripeToken, account, waldogram, config }) => {
    setCheckingOut(true);
    setCheckoutError('');
    setCheckoutSuccess(false);

    try {
      const primaryIdentity = account.linked_identities.records.find(
        identity => identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY,
      );

      await checkoutMutation({
        accountId: account.id,
        stripeTokenId: stripeToken.id,
        email: stripeToken.email,
        stripePaymentMethodId: stripeToken.stripe_payment_method_id,
        stripeConnectAccountId: stripeToken.stripe_connect_account_id,
        customPrintItems: [
          {
            album_custom_print_id: waldogram.id,
            identity_id: primaryIdentity.id,
            quantity: 1,
            include_shipping: true,
            configuration: {
              custom_print_caption: config.caption,
              photos: mapWaldogramConfigToPhotosInput(config),
              waldogram: {
                cabin_name: config.cabinName,
                identity_name: config.identityName,
              },
            },
          },
        ],
      });

      setCheckoutSuccess(true);
    } catch (error) {
      setCheckoutError(getErrorString(error));
    } finally {
      setCheckingOut(false);
    }
  };

  return {
    onCheckout,
    checkingOut,
    checkoutError,
    checkoutSuccess,
    setCheckingOut,
    setCheckoutError,
    setCheckoutSuccess,
  };
};

export default useWaldogramCheckout;
